import { ArrowDownTrayIcon, ChevronDownIcon, DocumentIcon } from '@heroicons/react/24/outline';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateApproval } from '@/api/approvals';
import { getProjectTeamMembers } from '@/api/Sherpa/Project/modules/ProjectTeam';
import { Layout } from '@/components/Layout';
import { LayoutCard, LayoutCardTitle } from '@/components/LayoutCard';
import { Accordion, AccordionItem } from '@/components/UI/Accordion';
import Badge from '@/components/UI/Badge';
import Button from '@/components/UI/Button';
import DropDown, { type DropDownMenuItem } from '@/components/UI/DropDown';
import { HookForm } from '@/components/UI/HookForm/Form';
import { HookReactSingleSelect } from '@/components/UI/HookForm/Form/ReactSelect';
import { HookTextarea } from '@/components/UI/HookForm/Form/TextArea';
import { HookFormProvider } from '@/components/UI/HookForm/Provider';
import Modal from '@/components/UI/Modal';
import { approvalAction } from '@/constants/Approval';
import { SherpaProjectReactQueryKey } from '@/constants/ReactQueryKeys';
import { authSelector } from '@/store';
import { notifyUser } from '@/store/Toast';
import type { UpdateApprovalPayload } from '@/types/Approvals';
import type { InternalTeam } from '@/types/Project';
import { capitalize, convertEnumToText, formatDate3 } from '@/utils';

interface IApprovalsProps {
  approvals: any;
  members: any;
  type: string;
}

const Approvals = ({ approvals, members, type }: IApprovalsProps) => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const auth = useSelector(authSelector);
  const {
    id,
    metaData: { projectId },
    status: approvalStatusValue,
    assignUser,
    createdBy,
  } = approvals[approvals.length - 1];

  const [showUserModal, setShowUserModal] = useState<boolean>(false);
  const [showRejectModal, setShowRejectModal] = useState<boolean>(false);
  const [showApproveModal, setShowApproveModal] = useState<boolean>(false);

  const toggleUserModal = () => {
    setShowUserModal((prev) => !prev);
  };

  const toggleRejectionModal = () => {
    setShowRejectModal((prev) => !prev);
  };

  const toggleApprovalModal = () => {
    setShowApproveModal((prev) => !prev);
  };

  const menuItems: DropDownMenuItem[] = Object.keys(approvalAction).map((key) => {
    return {
      clicked: () => {
        if (key === 'DELEGATED') {
          toggleUserModal();
        } else if (key === 'REJECTED') {
          toggleRejectionModal();
        } else if (key === 'APPROVED') {
          toggleApprovalModal();
        }
      },
      name: approvalAction[key as keyof typeof approvalAction],
    };
  });

  const updateApprovalMutation = useMutation(
    async (payload: UpdateApprovalPayload) => {
      await updateApproval(payload);
    },
    {
      onSuccess: () => {
        if (type === 'invoice') {
          queryClient.invalidateQueries([SherpaProjectReactQueryKey.GET_PROJECT_INVOICE]);
          queryClient.invalidateQueries(['ProjectDrawInvoice-audit']);
        } else {
          queryClient.invalidateQueries([SherpaProjectReactQueryKey.GET_PROJECT_DOCUMENT]);
          queryClient.invalidateQueries(['ProjectDrawDocument-audit']);
        }

        setShowUserModal(false);
        setShowApproveModal(false);
        setShowRejectModal(false);
      },
      onError: () => {
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: 'Failed to update Approval',
            toastType: 'failed',
          })
        );
      },
    }
  );

  const { mutate: mutateApproval } = updateApprovalMutation;

  const teamMembersQuery = useQuery(
    [SherpaProjectReactQueryKey.GET_PROJECT_MEMBERS, projectId],
    async () => {
      const response = await getProjectTeamMembers({
        projectId: projectId,
      });
      return response.data?.internal || [];
    },
    {
      enabled: !!projectId,
    }
  );

  const { data: teamMembers = [] } = teamMembersQuery;

  const updatedTeamMembers = teamMembers.filter((mem) => mem?.id !== auth?.data?.id);

  const handleSubmit = ({ assignedUser, note }: { assignedUser: InternalTeam; note: string }) => {
    mutateApproval({
      id: id as number,
      status: 'DELEGATED',
      assignedUserId: assignedUser.id,
      note,
    });
  };

  const handleRejection = ({ note }: { note: string }) => {
    mutateApproval({
      id: id as number,
      status: 'REJECTED',
      note,
    });
  };

  const handleApproval = ({ note }: { note: string }) => {
    mutateApproval({
      id: id as number,
      status: 'APPROVED',
      note,
    });
  };

  const renderApprovalDetails = ({
    files,
    uploadedBy,
    uploadedDate,
    lastEditedBy,
  }: {
    files: any;
    uploadedBy: any;
    uploadedDate: any;
    lastEditedBy: any;
  }) => {
    return files?.map((document: any) => {
      return (
        <div key={document.id}>
          <div className='flex py-3 gap-2 border-t-2'>
            <DocumentIcon className='w-10 h-10 flex-none' />
            <div className='flex flex-col gap-1 shrink overflow-hidden'>
              <p className='max-w-xs truncate' title={document?.name}>
                {capitalize(document?.name)}
              </p>
              {uploadedBy && <span className='text-neutralColor-normal text-xs'>Uploaded By: {uploadedBy}</span>}

              {uploadedDate && lastEditedBy && (
                <div className='flex flex-col justify-between gap-1'>
                  <span className='text-neutralColor-normal text-xs'>Uploaded : {uploadedDate}</span>
                  <span className='text-neutralColor-normal text-xs'>
                    Last Edited: {members?.find((member: any) => member?.id === lastEditedBy)?.name}
                  </span>
                </div>
              )}
            </div>
            <div className='grow'>
              <a href={document?.signedKey} download={document?.name} target='_blank' rel='noreferrer'>
                <ArrowDownTrayIcon className='w-5 h-5 cursor-pointer hover:text-blue-400' />
              </a>
            </div>
          </div>
        </div>
      );
    });
  };

  return (
    <Layout>
      <LayoutCard>
        <LayoutCardTitle
          title='Approvals'
          actions={
            <div className='flex gap-2 items-center'>
              {approvalStatusValue !== 'APPROVED' && approvalStatusValue !== 'REJECTED' && assignUser?.id === auth.data?.id && (
                <DropDown menuItems={menuItems}>
                  <div className='flex gap-2 items-center border p-2 rounded-md w-40 justify-between font-medium'>
                    <span>Actions</span>
                    <ChevronDownIcon className='h-4 w-4' />
                  </div>
                </DropDown>
              )}
            </div>
          }
        />
        <div className='h-screen/2 overflow-y-scroll p-2'>
          <Accordion>
            {approvals?.map((approval: any) => {
              return (
                <AccordionItem
                  key={approval?.id}
                  id={approval?.id}
                  title={
                    <div className='flex justify-between pr-2'>
                      <h3 className=''>Version: {approval?.version}</h3>
                      <Badge status={approval?.status} text={convertEnumToText(approval?.status)} />
                    </div>
                  }
                >
                  <div className='space-y-3'>
                    <div className='flex justify-between items-start'>
                      <div className='space-y-3'>
                        <div className='flex justify-between items-center gap-3'>
                          <span className='text-xs text-neutralColor-normal'>Assigned To: {approval?.assignUser?.name}</span>
                          <span className='text-xs text-neutralColor-normal'>Assigned Date: {formatDate3(approval?.createdAt)}</span>
                        </div>
                      </div>
                    </div>
                    <Accordion>
                      <AccordionItem id='notes-version' title={<h4 className='font-medium'>Notes</h4>}>
                        {approval?.note ?? 'None'}
                      </AccordionItem>
                    </Accordion>
                    <Accordion multi>
                      {approval?.metaData?.documents ? (
                        <AccordionItem
                          id={`${type}-version`}
                          triggerClassName='py-4'
                          customClassName='py-0'
                          title={
                            <div className='flex justify-between items-start'>
                              <h4 className='font-medium'>Documents</h4>
                              <span>{approval?.metaData?.documents?.length}</span>
                            </div>
                          }
                        >
                          {renderApprovalDetails({
                            files: approval?.metaData?.documents,
                            uploadedBy: approval?.createdBy?.name,
                            uploadedDate: formatDate3(approval?.metaData?.lastEditedAt),
                            lastEditedBy: approval?.metaData?.lastEditedBy,
                          })}
                        </AccordionItem>
                      ) : (
                        <>
                          <AccordionItem
                            id={`${type}-version-invoices`}
                            triggerClassName='py-4'
                            customClassName='py-0'
                            title={
                              <div className='flex justify-between items-start'>
                                <h4 className='font-medium'>Invoices</h4>
                                <span>{approval?.metaData?.invoices?.length}</span>
                              </div>
                            }
                          >
                            {renderApprovalDetails({
                              files: approval?.metaData?.invoices,
                              uploadedBy: approval?.createdBy?.name,
                              uploadedDate: formatDate3(approval?.metaData?.lastEditedAt),
                              lastEditedBy: approval?.metaData?.lastEditedBy,
                            })}
                          </AccordionItem>
                          <AccordionItem
                            id={`${type}-version-additional-docs`}
                            triggerClassName='py-4'
                            customClassName='py-0'
                            title={
                              <div className='flex justify-between items-start'>
                                <h4 className='font-medium'>Additional Docs</h4>
                                <span>{approval?.metaData?.additionalDocs?.length}</span>
                              </div>
                            }
                          >
                            {renderApprovalDetails({
                              files: approval?.metaData?.additionalDocs,
                              uploadedBy: approval?.createdBy?.name,
                              uploadedDate: formatDate3(approval?.metaData?.lastEditedAt),
                              lastEditedBy: approval?.metaData?.lastEditedBy,
                            })}
                          </AccordionItem>
                        </>
                      )}
                    </Accordion>
                  </div>
                </AccordionItem>
              );
            })}
          </Accordion>
        </div>
        {showUserModal && (
          <Modal>
            <HookFormProvider>
              <HookForm onSubmit={handleSubmit}>
                <p className='text-center'>Are you sure you want to delegate this invoice</p>
                <p className='text-center text-primaryColor-normal py-2'>Please confirm that you would like to make these changes</p>
                <HookReactSingleSelect
                  label='Delegate To'
                  name='assignedUser'
                  required
                  options={updatedTeamMembers}
                  getOptionLabel={(option) => (option as InternalTeam).name}
                  getOptionValue={(option) => `${(option as InternalTeam).id}`}
                />
                <div className='mt-4'>
                  <HookTextarea name='note' label='Delegation Note' />
                </div>
                <div className='flex gap-2 justify-end w-full mt-5'>
                  <Button
                    label='Cancel'
                    btnStyle='btn-neutral'
                    btnType='button'
                    disabled={updateApprovalMutation.isLoading}
                    onClick={() => {
                      toggleUserModal();
                    }}
                  />
                  <Button
                    label={`${updateApprovalMutation.isLoading ? 'Please Wait...' : 'Delegate'}`}
                    btnStyle='btn-primary'
                    btnType='submit'
                    disabled={updateApprovalMutation.isLoading}
                  />
                </div>
              </HookForm>
            </HookFormProvider>
          </Modal>
        )}
        {showRejectModal && (
          <Modal>
            <HookFormProvider>
              <HookForm onSubmit={handleRejection}>
                <p className='text-center'>Are you sure you want to reject this invoice</p>
                <p className='text-center text-primaryColor-normal py-2'>Please confirm that you would like to make these changes</p>
                <div className='mt-5'>
                  <HookTextarea name='note' label='Rejection Note' required />
                </div>
                <div className='flex gap-2 justify-end w-full mt-5 border-t-2 pt-4'>
                  <Button
                    label='Cancel'
                    btnStyle='btn-neutral'
                    btnType='button'
                    disabled={updateApprovalMutation.isLoading}
                    onClick={() => {
                      toggleRejectionModal();
                    }}
                  />
                  <Button
                    label={`${updateApprovalMutation.isLoading ? 'Please Wait...' : 'Reject'}`}
                    btnStyle='btn-primary'
                    btnType='submit'
                    disabled={updateApprovalMutation.isLoading}
                  />
                </div>
              </HookForm>
            </HookFormProvider>
          </Modal>
        )}
        {showApproveModal && (
          <Modal>
            <HookFormProvider>
              <HookForm onSubmit={handleApproval}>
                <p className='text-center'>Are you sure you want to Approve this invoice</p>
                <p className='text-center text-primaryColor-normal py-2'>Please confirm that you would like to make these changes </p>
                <div className='mt-5'>
                  <HookTextarea name='note' label='Approval Note' />
                </div>
                <div className='flex gap-2 justify-end w-full mt-5 border-t-2 pt-4'>
                  <Button
                    label='Cancel'
                    btnStyle='btn-neutral'
                    btnType='button'
                    disabled={updateApprovalMutation.isLoading}
                    onClick={() => {
                      toggleApprovalModal();
                    }}
                  />
                  <Button
                    label={`${updateApprovalMutation.isLoading ? 'Please Wait...' : 'Confirm'}`}
                    btnStyle='btn-primary'
                    btnType='submit'
                    disabled={updateApprovalMutation.isLoading}
                  />
                </div>
              </HookForm>
            </HookFormProvider>
          </Modal>
        )}
      </LayoutCard>
    </Layout>
  );
};

export default Approvals;
